import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {FormCategorySimpleResponse} from 'src/api/ProactApiClient';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {
  useDeleteFormCategoryMutation,
  useGetFormCategoryListV2Query,
} from 'src/redux/queries/formCategory';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {FormCategoryModal} from './FormCategoryModal';

export const FormCategoryTable: React.FC = () => {
  const {setAlert} = useAlert();
  const perms = useModulePermissions(ModuleEnum.Forms);

  const [editTarget, setEditTarget] = useState<FormCategorySimpleResponse>();
  const [modalOpened, modalHandlers] = useDisclosure(false);
  const [deleteTarget, setDeleteTarget] =
    useState<FormCategorySimpleResponse>();
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const [deleteFormCategory, {isLoading: isDeleting}] =
    useDeleteFormCategoryMutation();

  const columns = useMemo<MRT_ColumnDef<FormCategorySimpleResponse>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'form-category-v2',
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetFormCategoryListV2Query({
      start: start,
      length: length,
      filters: filters,
      filterFns: filterFns,
      sorting: sorting,
    });
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;
  const hasActiveFilters = filters.length > 0;

  const openCreate = () => {
    setEditTarget(undefined);
    modalHandlers.open();
  };

  const openEdit = (category: FormCategorySimpleResponse) => {
    setEditTarget(category);
    modalHandlers.open();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    confirmDeleteHandlers.close();

    try {
      await deleteFormCategory(deleteTarget.id).unwrap();
      setAlert(`${deleteTarget.name} was successfully deleted.`, 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setDeleteTarget(undefined);
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      getRowId: row => String(row.id),
      enableRowActions: perms.update || perms.delete,
      renderRowActions: ({row}) => (
        <TableRowActions>
          {perms.update && (
            <TableRowActions.Edit onClick={() => openEdit(row.original)} />
          )}
          {perms.delete && (
            <TableRowActions.Menu>
              <TableRowActions.MenuItem
                color="red"
                onClick={() => {
                  setDeleteTarget(row.original);
                  confirmDeleteHandlers.open();
                }}>
                Delete
              </TableRowActions.MenuItem>
            </TableRowActions.Menu>
          )}
        </TableRowActions>
      ),
      renderEmptyRowsFallback: () =>
        hasActiveFilters ? (
          <NoItemsPanel noItemsText="No categories match these filters." />
        ) : (
          <NoItemsPanel
            heading="No categories yet"
            noItemsText={
              perms.create
                ? 'Create a category to group forms on the My Forms tab.'
                : 'Categories group forms on the My Forms tab. None have been set up yet.'
            }
            canCreate={perms.create}
            createNode={
              perms.create ? (
                <Button onClick={openCreate}>New Category</Button>
              ) : undefined
            }
          />
        ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {perms.create && (
              <TableToolbar.Action variant="create" onClick={openCreate}>
                New Category
              </TableToolbar.Action>
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <>
      <Text size="sm" c="dimmed" mb="sm">
        Categories group forms on the My Forms tab. Deleting a category moves
        its forms to Uncategorized.
      </Text>
      {table}

      <FormCategoryModal
        opened={modalOpened}
        category={editTarget}
        onClose={modalHandlers.close}
      />

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        confirmationButtonDisabled={isDeleting}
        title={`Delete ${deleteTarget?.name ?? 'this category'}?`}
        body="Forms in this category become Uncategorized. The forms themselves are not deleted. This action cannot be undone."
      />
    </>
  );
};
